import React, { createContext, useContext, useEffect, useState } from 'react';
import { fireCartConfetti } from '../lib/confetti';

const CartContext = createContext(null);

const STORAGE_KEY = 'nnl_cart';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const lineKey = (product, length, color, density) => `${product.id}__${length || ''}__${color || ''}__${density || ''}`;

  const addItem = (product, { length, color, density, quantity = 1, price, confettiAt } = {}) => {
    const key = lineKey(product, length, color, density);
    fireCartConfetti(confettiAt?.x, confettiAt?.y);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) => (i.key === key ? { ...i, quantity: i.quantity + quantity } : i));
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          price: price != null ? price : product.price,
          image: product.image,
          category: product.category,
          collection: product.collection || null,
          sale: product.sale || null,
          length: length || null,
          color: color || null,
          density: density || null,
          quantity,
        },
      ];
    });
    setIsOpen(true);
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const updateQuantity = (key, quantity) => {
    if (quantity <= 0) return removeItem(key);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity } : i)));
  };

  const clearCart = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  // Braiding Hair — Buy 4, Get 1 Free (one free pack per order: the cheapest braiding pack)
  const braidItems = items.filter((i) => i.category === 'braiding-hair');
  const braidQty = braidItems.reduce((s, i) => s + i.quantity, 0);
  const discount = braidQty >= 5 ? Math.min(...braidItems.map((i) => i.price)) : 0;
  const freeAwayCount = braidQty > 0 && braidQty < 5 ? 5 - braidQty : 0;

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clearCart, count, subtotal, discount, braidQty, freeAwayCount, isOpen, setIsOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
